import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  confirm,
  Modal,
  toast,
} from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import {
  ArrowLeftIcon,
  MapPinIcon,
  PencilIcon,
  PlusIcon,
  Trash2Icon,
} from "lucide-react";
import { useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { QueryState } from "@/shared/ui/QueryState";
import type { BranchItem } from "../api/client";
import {
  useBranchesQuery,
  useCreateBranchMutation,
  useDeleteBranchMutation,
  useSetBranchActiveMutation,
  useUpdateBranchMutation,
} from "../api/queries";
import {
  BRANCH_CURRENCY_INHERIT,
  BranchForm,
  type BranchFormValues,
} from "../ui/BranchForm";

export function ShopBranchesPage() {
  const nav = useNavigate({ from: "/app/shops/$id/branches" });
  const { id: shopId } = useParams({ from: "/app/shops/$id/branches" });

  const { data, ...result } = useBranchesQuery(shopId ?? "");
  const setActive = useSetBranchActiveMutation(shopId ?? "");
  const createBranch = useCreateBranchMutation(shopId ?? "");
  const updateBranch = useUpdateBranchMutation(shopId ?? "");
  const deleteBranchMutation = useDeleteBranchMutation(shopId ?? "");

  const canUpdate = useActionPermission(["branches:update"]);
  const canCreate = useActionPermission(["branches:create"]);
  const canDelete = useActionPermission(["branches:delete"]);

  const [addOpen, setAddOpen] = useState(false);
  const [editBranch, setEditBranch] = useState<BranchItem | null>(null);

  const branches = data?.branches ?? [];
  const shopName = data?.shopName ?? "ກຳລັງໂຫຼດ...";

  const handleToggle = async (branch: BranchItem) => {
    try {
      await setActive.mutateAsync({
        branchId: branch.id,
        isActive: !branch.isActive,
      });
      toast.success(branch.isActive ? "ປິດສາຂາສຳເລັດ" : "ເປີດສາຂາສຳເລັດ");
    } catch {
      // fetcher shows error toast
    }
  };

  const handleCreate = async (values: BranchFormValues) => {
    try {
      await createBranch.mutateAsync({
        name: values.name,
        phone: values.phone || null,
        address: values.address || null,
        isActive: values.isActive,
        defaultCurrency:
          values.defaultCurrency === BRANCH_CURRENCY_INHERIT
            ? null
            : (values.defaultCurrency as "LAK" | "THB" | "USD"),
      });
      setAddOpen(false);
      toast.success("ເພີ່ມສາຂາສຳເລັດ");
    } catch {
      // fetcher shows error toast
    }
  };

  const handleUpdate = async (values: BranchFormValues) => {
    if (!editBranch) return;
    try {
      await updateBranch.mutateAsync({
        branchId: editBranch.id,
        input: {
          name: values.name,
          phone: values.phone || null,
          address: values.address || null,
          isActive: values.isActive,
          defaultCurrency:
            values.defaultCurrency === BRANCH_CURRENCY_INHERIT
              ? null
              : (values.defaultCurrency as "LAK" | "THB" | "USD"),
        },
      });
      setEditBranch(null);
      toast.success("ແກ້ໄຂສາຂາສຳເລັດ");
    } catch {
      // fetcher shows error toast
    }
  };

  const handleDeleteClick = async (branch: BranchItem) => {
    const ok = await confirm({
      title: "ລົບສາຂາ",
      description: `ທ່ານແນ່ໃຈບໍ່ວ່າຕ້ອງການລົບສາຂາ "${branch.name}"? ພະນັກງານທີ່ຜູກພັນກັບສາຂານີ້ຈະຖືກປ່ຽນເປັນບໍ່ມີສາຂາ.`,
      actionText: "ລົບ",
      ActionProps: { variant: "destructive" },
    });
    if (!ok) return;
    try {
      await deleteBranchMutation.mutateAsync(branch.id);
      toast.success("ລົບສາຂາສຳເລັດ");
    } catch {
      // fetcher shows error toast
    }
  };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ຮ້ານຄ້າ", to: "/app/shops" },
            {
              label: shopName,
              to: "/app/shops/$id",
              params: { id: shopId ?? "" },
            },
            { label: "ຈັດການສາຂາ" },
          ]}
        />
      </Header>

      <Main>
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h2 className="font-bold text-2xl tracking-tight">
              ສາຂາທັງໝົດ: <span className="text-secondary">{shopName}</span>
            </h2>
            <p className="text-muted-foreground">
              ເພີ່ມ / ແກ້ໄຂ / ລົບສາຂາ ແລະປັບສະຖານະເປີດປິດ
            </p>
          </div>
          <div className="flex gap-2">
            {canCreate && (
              <Button
                onClick={() => setAddOpen(true)}
                className="bg-[#F26522] text-white hover:bg-[#F26522]/90"
              >
                <PlusIcon className="mr-2 size-4" />
                ເພີ່ມສາຂາ
              </Button>
            )}
            <Button variant="outline" onClick={() => nav({ to: "/app/shops" })}>
              <ArrowLeftIcon className="size-4" />
              ກັບຄືນລາຍການ
            </Button>
          </div>
        </div>

        <QueryState result={result} isEmpty={false}>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 font-medium text-secondary text-sm uppercase tracking-wide">
                <MapPinIcon className="size-4" />
                ລາຍການສາຂາ ({branches.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {branches.length === 0 ? (
                <p className="py-8 text-center text-muted-foreground text-sm">
                  ບໍ່ມີສາຂາ
                  {canCreate && " — ກົດເພີ່ມສາຂາເພື່ອເລີ່ມຕົ້ນ"}
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="py-2 font-medium">ຊື່ສາຂາ</th>
                        <th className="py-2 font-medium">ທີ່ຢູ່</th>
                        <th className="py-2 font-medium">ເບີໂທ</th>
                        <th className="py-2 font-medium">ສະກຸນ</th>
                        <th className="py-2 text-right font-medium">ສະຖານະ</th>
                        {(canUpdate || canDelete) && (
                          <th className="w-40 py-2 text-right font-medium">
                            ການກະທໍາ
                          </th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {branches.map((branch) => (
                        <tr key={branch.id} className="border-b last:border-0">
                          <td className="py-2 font-medium">{branch.name}</td>
                          <td className="py-2 text-muted-foreground">
                            {branch.address ?? "-"}
                          </td>
                          <td className="py-2 text-muted-foreground">
                            {branch.phone ?? "-"}
                          </td>
                          <td className="py-2 text-muted-foreground">
                            {branch.defaultCurrency ?? "ຕາມຮ້ານ"}
                          </td>
                          <td className="py-2 text-right">
                            <span
                              className={
                                branch.isActive
                                  ? "font-medium text-primary"
                                  : "text-muted-foreground"
                              }
                            >
                              {branch.isActive ? "ເປີດ" : "ປິດ"}
                            </span>
                          </td>
                          {(canUpdate || canDelete) && (
                            <td className="py-2 text-right">
                              <div className="flex justify-end gap-1">
                                {canUpdate && (
                                  <>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleToggle(branch)}
                                      disabled={setActive.isPending}
                                    >
                                      {branch.isActive ? "ປິດ" : "ເປີດ"}
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => setEditBranch(branch)}
                                      title="ແກ້ໄຂ"
                                    >
                                      <PencilIcon className="size-4" />
                                    </Button>
                                  </>
                                )}
                                {canDelete && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDeleteClick(branch)}
                                    disabled={deleteBranchMutation.isPending}
                                    className="text-destructive hover:bg-destructive/10"
                                    title="ລົບ"
                                  >
                                    <Trash2Icon className="size-4" />
                                  </Button>
                                )}
                              </div>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </QueryState>
      </Main>

      {canCreate && (
        <Modal
          open={addOpen}
          onOpenChange={setAddOpen}
          title="ເພີ່ມສາຂາ"
          description="ສ້າງສາຂາໃໝ່ສຳລັບຮ້ານຄ້າ"
          size="sm"
        >
          <BranchForm
            submitting={createBranch.isPending}
            onSubmit={handleCreate}
            submitLabel="ເພີ່ມສາຂາ"
          />
        </Modal>
      )}

      {canUpdate && editBranch && (
        <Modal
          open={!!editBranch}
          onOpenChange={(open) => !open && setEditBranch(null)}
          title="ແກ້ໄຂສາຂາ"
          description={`ແກ້ໄຂຂໍ້ມູນສາຂາ: ${editBranch.name}`}
          size="sm"
        >
          <BranchForm
            initialValues={{
              name: editBranch.name,
              phone: editBranch.phone ?? "",
              address: editBranch.address ?? "",
              isActive: editBranch.isActive,
              defaultCurrency: (editBranch.defaultCurrency ??
                BRANCH_CURRENCY_INHERIT) as BranchFormValues["defaultCurrency"],
            }}
            submitting={updateBranch.isPending}
            onSubmit={handleUpdate}
            submitLabel="ບັນທຶກ"
          />
        </Modal>
      )}
    </>
  );
}
